import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  arrivalVerdict,
  formatDay,
  lineAmount,
  lineFor,
  lineNames,
  zonedDay,
  type IMaterialStack,
  type SupplierNameOf,
} from '@d-orca/shared/client/logistics';
import type { Delivery } from '@interfaces/ILogistics';
import { VERDICT_TONE_CLASS } from '../board.logic';
import type { CardTranslate } from './boardCard.logic';
import { historyTotal } from './deliveryHistory.logic';
import { ExpandableListRow } from './ExpandableList';
import styles from './DeliveryHistory.module.css';

interface IHistoryRowProps {
  open: boolean;
  onToggle: () => void;
  supplierOf: SupplierNameOf;
  tz: string;
}

interface IDeliveryHistoryRowProps extends IHistoryRowProps {
  order: Delivery;
}

interface IMaterialHistoryRowProps extends IHistoryRowProps {
  stack: IMaterialStack;
}

/** Joins a row's meta parts with the page's " · " separator, skipping blanks. */
function metaLine(parts: ReactNode[]): ReactNode {
  return parts.filter(Boolean).map((part, index) => (
    <span key={index}>
      {index > 0 ? ' · ' : null}
      {part}
    </span>
  ));
}

/** The day a synced order arrived, and its verdict against the due date. A
 *  synced order always has `deliveredAt` — the sync is what stamps it. */
function arrival(order: Delivery, tz: string, t: CardTranslate, lang: string) {
  const day = zonedDay(order.deliveredAt!, tz);
  const verdict = arrivalVerdict(order.dueDate, day, t);
  return {
    date: formatDay(day, lang),
    verdict: verdict ? <span className={styles[VERDICT_TONE_CLASS[verdict.tone]]}>{verdict.text}</span> : null,
  };
}

/** One synced order: when it arrived, from whom, and what it carried. */
export function DeliveryHistoryRow({ order, open, onToggle, supplierOf, tz }: IDeliveryHistoryRowProps) {
  const { t, i18n } = useTranslation();
  const { date, verdict } = arrival(order, tz, t, i18n.language);
  const names = lineNames(order);
  return (
    <ExpandableListRow
      name={`#${order.ref} · ${order.destinationName}`}
      meta={metaLine([t('logistics.history.arrived', { date }), verdict, supplierOf(order)])}
      // One material: its amount. Several: how many, named underneath.
      amount={names.length === 1 ? lineAmount(order.lines[0]!, t) : t('logistics.history.materials', { count: names.length })}
      amountSub={names.join(t('common.listSeparator'))}
      open={open}
      onToggle={onToggle}
      ariaLabel={t('logistics.history.expandDelivery', { ref: order.ref })}
    >
      <ul className={styles.detail}>
        {order.lines.map((line) => (
          <li key={line.id}>
            <span>{line.itemName}</span>
            <b>{lineAmount(line, t)}</b>
          </li>
        ))}
      </ul>
    </ExpandableListRow>
  );
}

/** One material: its total over every synced order, unfolding to each one. */
export function MaterialHistoryRow({ stack, open, onToggle, supplierOf, tz }: IMaterialHistoryRowProps) {
  const { t, i18n } = useTranslation();
  const { key: material, face, history } = stack;
  const orders = [face, ...history];
  // Every order in a stack carries the stack's material.
  const faceLine = lineFor(face, material)!;
  const last = lineAmount(faceLine, t);
  return (
    <ExpandableListRow
      name={faceLine.itemName}
      meta={t('logistics.history.materialMeta', { date: arrival(face, tz, t, i18n.language).date, count: orders.length })}
      amount={historyTotal(orders, material, t)}
      amountSub={last ? t('logistics.history.lastDelivery', { amount: last }) : null}
      open={open}
      onToggle={onToggle}
      ariaLabel={t('logistics.history.expandMaterial', { material: faceLine.itemName })}
    >
      <ul className={styles.detail}>
        {orders.map((order) => {
          const { date, verdict } = arrival(order, tz, t, i18n.language);
          return (
            <li key={order.ref}>
              <span>{metaLine([date, `#${order.ref}`, verdict, supplierOf(order)])}</span>
              <b>{lineAmount(lineFor(order, material)!, t)}</b>
            </li>
          );
        })}
      </ul>
    </ExpandableListRow>
  );
}
